import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { formatAge } from '@/utils/ageHelper';
import dayjs from 'dayjs';
import { showLabel } from '@/utils/helper';
import { LANGUAGE_LIST, SEX_LIST, YESNOLIST } from '@/common/constants/common';
import {
	useDeleteFaceSheetMutation,
	useGetFaceSheetDetailQuery,
} from '@/api/forms-management/faceSheet';
import {
	useGetDicListQuery,
	useGetExportCustomerPDFMutation,
	useGetFaceSheetGenerateReportMutation,
} from '@/api/common';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function FaceSheetDetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, faceSheetId, admissionRequestId } = useParams();
	const showModal = useModal();
	const [deleteFaceSheet] = useDeleteFaceSheetMutation();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [personalInformationExpanded, setPersonalInformationExpanded] =
		useState(true);
	const [contactInformationExpanded, setContactInformationExpanded] =
		useState(true);
	const [familyInformationExpanded, setFamilyInformationExpanded] =
		useState(true);
	const [workInformationExpanded, setWorkInformationExpanded] = useState(true);
	const [memberInformationExpanded, setMemberInformationExpanded] =
		useState(true);
	const [caseInformationExpanded, setCaseInformationExpanded] = useState(true);
	const [referrerInformationExpanded, setReferrerInformationExpanded] =
		useState(true);
	const [shareInformationExpanded, setShareInformationExpanded] =
		useState(true);
	const [healthInformationExpanded, setHealthInformationExpanded] =
		useState(true);
	const [initialInformationExpanded, setInitialInformationExpanded] =
		useState(true);
	const [latestInformationExpanded, setLatestInformationExpanded] =
		useState(true);

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getFaceSheetGenerateReport] = useGetFaceSheetGenerateReportMutation();

	const { data: details }: any = useGetFaceSheetDetailQuery(faceSheetId || '', {
		refetchOnMountOrArgChange: true,
	});

	const columns: GridColDef[] = [
		{
			field: 'familyCarerName',
			headerName: t('preAdmission.name'),
			flex: 25,
		},
		{
			field: 'age',
			headerName: t('preAdmission.age'),
			flex: 25,
			renderCell: (params) => params.row.age || '-',
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 25,
		},
		{
			field: 'address',
			headerName: t('preAdmission.address'),
			flex: 25,
		},
		{
			field: 'telNo',
			headerName: t('preAdmission.telNo'),
			flex: 25,
		},
		{
			field: 'emergencyContact',
			headerName: t('preAdmission.emergencyContact'),
			flex: 25,
			renderCell: (params) => showLabel(params.row.emergencyContact, YESNOLIST),
		},
	];
	const columns2: GridColDef[] = [
		{
			field: 'clientName',
			headerName: t('preAdmission.clientName'),
			flex: 25,
		},
		{
			field: 'organization',
			headerName: t('preAdmission.organisation'),
			flex: 25,
		},
		{
			field: 'name',
			headerName: t('preAdmission.name'),
			flex: 25,
		},
		{
			field: 'telNo',
			headerName: t('preAdmission.telNo'),
			flex: 25,
		},
	];

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.faceSheet'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteFaceSheet(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.faceSheetDetails')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getFaceSheetGenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box display='flex' justifyContent='space-between' marginBottom={2}>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.serviceDetails')}
						</Typography>
					</Box>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.fileNumber')}
								value={details?.data.fileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.unit')}
								value={details?.data.branchName}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setPersonalInformationExpanded(!personalInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.personalInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: personalInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={personalInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfBirth')}
									value={
										details?.data.dateOfBirth
											? dayjs(details?.data.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.currentAge')}
									value={formatAge(
										details?.data.currentAge,
										details?.data.currentAgeEstimated
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.sex')}
									value={showLabel(details?.data.sex, SEX_LIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.HKID')}
									value={details?.data.hkid}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dialect')}
									value={details?.data.dialectDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.maritalStatus')}
									value={details?.data.maritalStatusDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.placeBirth')}
									value={details?.data.placeOfBirthDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.placeBirthSpecify')}
									value={details?.data.placeOfBirthSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.yearArrivingHK')}
									value={details?.data.yearOfArrivingHk}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setContactInformationExpanded(!contactInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.contactInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: contactInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={contactInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mobilePhone')}
									value={details?.data.phone}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.homeTel')}
									value={details?.data.homeTel}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.otherPhone')}
									value={details?.data.otherPhone}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.typeHousing')}
									value={details?.data.typeOfHousingDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.typeHousingSpecify')}
									value={details?.data.typeOfHousingSpecify}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.addressLanguage')}
									value={showLabel(
										details?.data.addressLanguage,
										LANGUAGE_LIST
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.region')}
									value={details?.data.regionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.district')}
									value={details?.data.districtDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.subDistrict')}
									value={details?.data.subdistrictDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.estate/court/village')}
									value={details?.data.estateCourtVillageDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/street')}
									value={details?.data.roadStreet}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.road/streetNo')}
									value={details?.data.roadStreetNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block')}
									value={details?.data.buildingBlockDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.building/block(specify)')}
									value={details?.data.buildingBlockSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.floor')}
									value={details?.data.floor}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.flat')}
									value={details?.data.flat}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.fullAddress')}
									value={details?.data.fullAddress}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setFamilyInformationExpanded(!familyInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.familyCarersInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: familyInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={familyInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<LocalDataGrid
									rows={details?.data.familyCarersList}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setWorkInformationExpanded(!workInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.educationWorkLivingStatus')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: workInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={workInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.financialCondition')}
									value={details?.data.financialConditionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.workStatus')}
									value={details?.data.workStatusDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.workMotivation')}
									value={details?.data.workMotivationDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.education')}
									value={details?.data.educationDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.studyingSecondarySchool')}
									value={
										details?.data.studyingSecondarySchool == null
											? ''
											: details.data.studyingSecondarySchool
												? 'Yes'
												: 'No'
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.studyingSecondarySchoolSpecify')}
									value={details?.data.chiFullNameOfSecondarySchool}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.attendingOtherYouthService')}
									value={
										details?.data.attendingOtherYouthService == null
											? ''
											: details.data.attendingOtherYouthService
												? 'Yes'
												: 'No'
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.attendingOtherYouthServiceSpecify')}
									value={details?.data.nameOfOtherYouthService}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.livingStatus')}
									value={details?.data.livingStatusDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.livingStatusSpecify')}
									value={details?.data.nameOfResidentialCareHome}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.livingWithPersonAgedUnder18')}
									value={details?.data.livingWithPersonsAgedUnder18Desc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.excludePrincipalClient')}
									value={details?.data.noOfPeopleLivingTogether}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.degreeFamilySupport')}
									value={details?.data.degreeOfFamilySupportDesc}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setMemberInformationExpanded(!memberInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.membershipInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: memberInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={memberInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mailRequest')}
									value={showLabel(details?.data.mailRequest, YESNOLIST)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.newsletterConfidential')}
									value={showLabel(
										details?.data.newsletterConfidential,
										YESNOLIST
									)}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setCaseInformationExpanded(!caseInformationExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.caseStatus')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: caseInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={caseInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfReceivingReferral/Application')}
									value={
										details?.data.receivingReferralOrApplicationDate
											? dayjs(details?.data.receivingReferralOrApplicationDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateCaseOpen')}
									value={details?.data.dateOfCaseOpen}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateConsentObtained')}
									value={
										details?.data.dateOfConsentObtained
											? dayjs(details?.data.dateOfConsentObtained)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setReferrerInformationExpanded(!referrerInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.referrerInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: referrerInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={referrerInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.approachSource')}
									value={details?.data.approachSourceDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals')}
									value={details?.data.profileOfReferralDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.profileOfReferrals(specify)')}
									value={details?.data.profileOfReferralSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency')}
									value={details?.data.referralAgencyDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referralAgency(specify)')}
									value={details?.data.referralAgencySpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.prefix')}
									value={details?.data.prefixOfReferrerDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameOfReferrer')}
									value={details?.data.nameOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.positionOfReferrer')}
									value={details?.data.positionOfReferrer}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.referrerContactNo')}
									value={details?.data.referrerContactNo}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.referrerFaxNo')}
									value={details?.data.referrerFaxNo}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setShareInformationExpanded(!shareInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.shareCaseService')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: shareInformationExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={shareInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.otherOrganisationServingSameCase')}
									value={showLabel(
										details?.data.otherOrganisationServing,
										YESNOLIST
									)}
								/>
							</Grid>
							{details?.data.otherOrganisationServing ? (
								<Grid size={12}>
									<LocalDataGrid
										rows={details?.data.organisationList}
										columns={columns2}
										getRowId={(row) => row.id}
										pagination={false}
									/>
								</Grid>
							) : (
								''
							)}
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setHealthInformationExpanded(!healthInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.mentalHealthInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: healthInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={healthInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.followUpClinicPsychiatry')}
									value={details?.data.followUpClinicPsychiatryDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mentalHealthConditionOther')}
									value={details?.data.mentalHealthConditionDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mainDiagnosis')}
									value={details?.data.mainDiagnosisDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.mainDiagnosisSpecify')}
									value={details?.data.mainDiagnosisSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.secondaryDiagnosis')}
									value={details?.data.secondaryDiagnosisDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.secondaryDiagnosisSpecify')}
									value={details?.data.secondaryDiagnosisSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.suspectedMentalHealthProblem')}
									value={details?.data.suspectedMentalHealthProblemDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.suspectedMentalHealthProblemSpecify')}
									value={details?.data.suspectedMentalHealthProblemSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.suicideIdeation')}
									value={details?.data.suicideIdeationDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.suicideHistory')}
									value={details?.data.suicideHistoryDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.violenceIdeation')}
									value={details?.data.violenceIdeationDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.violenceHistory')}
									value={details?.data.violenceHistoryDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.abuseHistory')}
									value={details?.data.abuseHistoryDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.abuseRole')}
									value={details?.data.abuseRoleDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.abuseType')}
									value={details?.data.abuseTypeDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.abuseTypeSpecify')}
									value={details?.data.abuseTypeSpecify}
								/>
							</Grid>
							<Grid size={12}>
								<AppFieldView
									label={t('preAdmission.addictionProblem')}
									value={details?.data.addictionProblemDesc}
								/>
							</Grid>

							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.typeAddictionProblem')}
									value={details?.data.typeOfAddictionProblemDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.typeAddictionProblemSpecify')}
									value={details?.data.typeOfAddictionProblemSpecify}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.coreConcern')}
									value={details?.data.coreConcernDesc}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateLinkUpHealthSystem')}
									value={
										details?.data.dateOfLinkUpToHealthSystem
											? dayjs(details?.data.dateOfLinkUpToHealthSystem)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.familyMembersHaveHistoryMI')}
									value={
										details?.data.familyMembersHaveHistoryOfMentalIllnessDesc
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.OtherIllnessDisability')}
									value={details?.data.otherIllnessDisability}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(EnquiryRecord)')}
									value={details?.data.enquiryRecordRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(MembershipApplication)')}
									value={details?.data.membershipApplicationRemarks}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.remarks(FaceSheet)')}
									value={details?.data.faceSheetRemarks}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setInitialInformationExpanded(!initialInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.initialFaceSheetInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: initialInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={initialInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.nameWorker')}
									value={details?.data.initialFaceSheetWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.rankWorker')}
									value={details?.data.initialFaceSheetWorkerRank}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.date')}
									value={
										details?.data.initialFaceSheetDate
											? dayjs(details?.data.initialFaceSheetDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() =>
							setLatestInformationExpanded(!latestInformationExpanded)
						}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.latestFaceSheetInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: latestInformationExpanded
									? 'rotate(180deg)'
									: 'none',
							}}
						/>
					</Box>
					<Collapse in={latestInformationExpanded}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name')}
									value={details?.data.latestFaceSheetWorkerName}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.rankUpdatedWorker')}
									value={details?.data.latestFaceSheetWorkerRank}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.updatedDate')}
									value={
										details?.data.latestFaceSheetDate
											? dayjs(details?.data.latestFaceSheetDate)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
